import React, { useEffect, useState } from "react";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import FacilityConstants from "../../utilities/constants/FacilityConstants";
import UserProfileImage from "./UserProfileImage";
import { useUser } from "../user/UserProvider";
import { updateUser } from "../user/userService";
import { BIRTH_DATE } from "../../utilities/constants/Regex";
import {
  isDateInThePast,
  isDateStringValid,
} from "../../utilities/functions/Helpers";
import { ToastContainer } from "react-toastify";
import {
  User,
  MapPin,
  Mail,
  Phone,
  Calendar,
  Edit2,
  Save,
  X,
} from "lucide-react";

const schema = object().shape({
  id: string().notRequired(),
  userId: string().required(),
  firstName: string().required("First name is required"),
  lastName: string().required("Last name is required"),
  email: string().email("Invalid email format.").required("Email is required"),
  phone: string().required("Phone is required"),
  username: string().required("Username is required"),
  gender: string().notRequired(),
  dateOfBirth: string()
    .test(
      "has-right-format",
      "Date must be formatted as 'mm-dd-yyyy'",
      (value) => BIRTH_DATE.test(value),
    )
    .test("is-date-in-the-past", "Date must be in the past", (value) =>
      isDateInThePast(value),
    )
    .test("is-a-valid-date", "Date must be a valid date", (value) =>
      isDateStringValid(value),
    )
    .required("Date of birth is required"),
  street: string().required("Street is required"),
  city: string().required("City is required"),
  state: string().required("State is required"),
  zipCode: string().required("Zip is required"),
});

const UserProfile = () => {
  const { userProfile, setUserProfile } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [apiError, setApiError] = useState(false);
  const usStates = Object.keys(FacilityConstants.STATES);
  const genders = Object.values(FacilityConstants.GENDERS);

  const {
    control,
    setValue,
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: userProfile.id,
      userId: userProfile.userId,
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      email: userProfile.email,
      phone: userProfile.phone,
      username: userProfile.username,
      gender: userProfile.gender,
      dateOfBirth: userProfile.dateOfBirth,
      street: userProfile.street,
      city: userProfile.city,
      state: userProfile.state,
      zipCode: userProfile.zipCode,
    },
  });

  const onSubmit = async (data) => {
    await updateUser(data, setUserProfile, setApiError);
    if (apiError) {
      return;
    } else {
      setIsEditing(false);
    }
  };

  const toggleEditing = () => {
    isEditing && reset();
    setIsEditing(!isEditing);
  };

  useEffect(() => {
    if (userProfile) {
      setValue("id", userProfile.id);
      setValue("userId", userProfile.userId);
      setValue("firstName", userProfile.firstName);
      setValue("lastName", userProfile.lastName);
      setValue("email", userProfile.email);
      setValue("phone", userProfile.phone);
      setValue("username", userProfile.username);
      setValue("gender", userProfile?.gender ?? "");
      setValue("dateOfBirth", userProfile.dateOfBirth);
      setValue("street", userProfile.street);
      setValue("city", userProfile.city);
      setValue("state", userProfile?.state ?? "");
      setValue("zipCode", userProfile.zipCode);
    }
  }, [userProfile, setValue]);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-serif font-bold text-slate-900 dark:text-white">
            Profile Settings
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage your personal information and preferences.
          </p>
        </div>
        <button
          type="button"
          onClick={toggleEditing}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all shadow-sm ${
            isEditing
              ? "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-blue-500/25 hover:-translate-y-0.5"
          }`}
        >
          {isEditing ? (
            <>
              <X size={18} /> Cancel
            </>
          ) : (
            <>
              <Edit2 size={18} /> Edit Profile
            </>
          )}
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
        {/* Profile Header Image */}
        <div className="h-40 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 relative">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="absolute -bottom-16 left-8 md:left-12">
            <div className="p-1.5 bg-white dark:bg-slate-900 rounded-full">
              <UserProfileImage
                imageUri={userProfile?.userMedia?.mediaUrl}
                userFullName={`${userProfile.firstName} ${userProfile.lastName}`}
                className="w-32 h-32 rounded-full border-4 border-white dark:border-slate-900 shadow-md"
              />
            </div>
          </div>
          <div className="absolute bottom-4 right-6 text-white/60 text-sm font-medium backdrop-blur-sm bg-black/20 px-3 py-1 rounded-full border border-white/10">
            Member since {new Date().getFullYear()}
          </div>
        </div>

        {/* Form Content */}
        <div className="pt-24 pb-12 px-8 md:px-12">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
            {/* Personal Info Section */}
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-6 border-b border-slate-100 dark:border-slate-800 pb-4">
                <User className="text-blue-500" size={24} />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {/* First Name */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    First Name
                  </label>
                  <input
                    {...register("firstName")}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.firstName ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500`}
                    disabled={!isEditing}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    Last Name
                  </label>
                  <input
                    {...register("lastName")}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.lastName ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500`}
                    disabled={!isEditing}
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.lastName.message}
                    </p>
                  )}
                </div>

                {/* Date of Birth */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    Date of Birth
                  </label>
                  <div className="relative">
                    <input
                      placeholder="mm-dd-yyyy"
                      {...register("dateOfBirth")}
                      className={`w-full px-4 py-3 rounded-xl border ${errors.dateOfBirth ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500`}
                      disabled={!isEditing}
                    />
                    <Calendar
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                      size={18}
                    />
                  </div>
                  {errors.dateOfBirth && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.dateOfBirth.message}
                    </p>
                  )}
                </div>

                {/* Gender */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    Gender
                  </label>
                  {isEditing ? (
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <div className="relative">
                          <select
                            {...field}
                            className={`w-full px-4 py-3 rounded-xl border ${errors.gender ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none`}
                            disabled={!isEditing}
                          >
                            <option value="">Select Gender</option>
                            {genders.map((g, i) => (
                              <option key={i} value={g}>
                                {g}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    />
                  ) : (
                    <input
                      {...register("gender")}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400"
                      disabled
                    />
                  )}
                  {errors.gender && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.gender.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact & Address Section */}
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-6 border-b border-slate-100 dark:border-slate-800 pb-4">
                <MapPin className="text-blue-500" size={24} />
                Contact & Address
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {/* Email */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      {...register("email")}
                      type="email"
                      className={`w-full pl-11 pr-4 py-3 rounded-xl border ${errors.email ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500`}
                      disabled={!isEditing}
                    />
                    <Mail
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                      size={18}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    Phone Number
                  </label>
                  <div className="relative">
                    <input
                      {...register("phone")}
                      className={`w-full pl-11 pr-4 py-3 rounded-xl border ${errors.phone ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500`}
                      disabled={!isEditing}
                    />
                    <Phone
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                      size={18}
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.phone.message}
                    </p>
                  )}
                </div>

                {/* Street */}
                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    Street Address
                  </label>
                  <input
                    {...register("street")}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.street ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500`}
                    disabled={!isEditing}
                  />
                  {errors.street && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.street.message}
                    </p>
                  )}
                </div>

                {/* City */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    City
                  </label>
                  <input
                    {...register("city")}
                    className={`w-full px-4 py-3 rounded-xl border ${errors.city ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500`}
                    disabled={!isEditing}
                  />
                  {errors.city && (
                    <p className="text-red-500 text-xs ml-1">
                      {errors.city.message}
                    </p>
                  )}
                </div>

                {/* State & Zip Row */}
                <div className="flex gap-4">
                  <div className="flex-1 space-y-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                      State
                    </label>
                    {isEditing ? (
                      <Controller
                        name="state"
                        control={control}
                        render={({ field }) => (
                          <div className="relative">
                            <select
                              {...field}
                              className={`w-full px-4 py-3 rounded-xl border ${errors.state ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none`}
                              disabled={!isEditing}
                            >
                              <option value="">State</option>
                              {usStates.map((s, i) => (
                                <option key={i} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      />
                    ) : (
                      <input
                        {...register("state")}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400"
                        disabled
                      />
                    )}
                  </div>
                  <div className="w-1/3 space-y-2">
                    <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                      Zip
                    </label>
                    <input
                      {...register("zipCode")}
                      className={`w-full px-4 py-3 rounded-xl border ${errors.zipCode ? "border-red-500" : "border-slate-200 dark:border-slate-700"} bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none disabled:bg-slate-50 disabled:text-slate-500 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500`}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex justify-end pt-6 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="submit"
                  className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/30 flex items-center gap-2 hover:-translate-y-0.5"
                >
                  <Save size={20} /> Save Changes
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
      <ToastContainer position="bottom-right" theme="colored" />
    </div>
  );
};

export default UserProfile;
